import { useEffect } from "react";
import { type Control, useController, useWatch } from "react-hook-form";

import type { CustomerInfoInput, SignersInput } from "../Signers.schema";

// ---------------------------------------------------------------------------
// useMirrorAddress
//
// Encapsulates the "Same as current" physical→mailing mirror logic for a single
// person column. Watches all six physical fields and the mailingSameAsCurrent
// checkbox; when the checkbox is checked the mailing fields are kept in sync
// via useEffect. Mailing inputs are disabled in the UI while the checkbox is
// checked (that disabled prop is driven by the returned `mailingSameAsCurrent`
// value in the parent component).
//
// Returns `mailingSameAsCurrent` (boolean) so the caller can both disable the
// mailing AddressFields and render the checkbox checked state.
// ---------------------------------------------------------------------------

type PersonPath = "driver" | "secondSigner";

/**
 * Builds a typed RHF field path into a person's customerInfo.
 * @param {PersonPath} personPath - Which person column (driver / secondSigner).
 * @param {keyof CustomerInfoInput} field - The customerInfo field name.
 * @returns {string} The dotted field path.
 */
function getCustomerFieldPath(
  personPath: PersonPath,
  field: keyof CustomerInfoInput
): `driver.customerInfo.${keyof CustomerInfoInput}` | `secondSigner.customerInfo.${keyof CustomerInfoInput}` {
  return `${personPath}.customerInfo.${field}` as
    | `driver.customerInfo.${keyof CustomerInfoInput}`
    | `secondSigner.customerInfo.${keyof CustomerInfoInput}`;
}

interface UseMirrorAddressOptions {
  control: Control<SignersInput>;
  personPath: PersonPath;
}

interface UseMirrorAddressResult {
  /** Current value of the mailingSameAsCurrent checkbox — use to disable the mailing AddressFields. */
  mailingSameAsCurrent: boolean;
  /** Handler for the "Same as current" checkbox's onCheckedChange event. */
  onSameAsCurrentChange: (checked: boolean | "indeterminate") => void;
}

/**
 * Drives the "Same as current" physical→mailing address mirror for one person.
 * @param {UseMirrorAddressOptions} options - The RHF control and person path.
 * @returns {UseMirrorAddressResult} The checkbox state and its change handler.
 */
export function useMirrorAddress({ control, personPath }: UseMirrorAddressOptions): UseMirrorAddressResult {
  // Watch the checkbox and all six physical fields reactively.
  const mailingSameAsCurrent = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "mailingSameAsCurrent"),
  });

  const physicalStreetNumber = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalStreetNumber") });
  const physicalStreetName = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalStreetName") });
  const physicalApartmentNumber = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "physicalApartmentNumber"),
  });
  const physicalZip = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalZip") });
  const physicalCity = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalCity") });
  const physicalState = useWatch({ control, name: getCustomerFieldPath(personPath, "physicalState") });

  // useController instances for the six mailing fields — the only writers when
  // the checkbox is checked (AddressFields inputs are disabled in that state).
  const { field: mailingStreetNumberField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingStreetNumber"),
  });
  const { field: mailingStreetNameField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingStreetName"),
  });
  const { field: mailingApartmentNumberField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingApartmentNumber"),
  });
  const { field: mailingZipField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingZip"),
  });
  const { field: mailingCityField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingCity"),
  });
  const { field: mailingStateField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingState"),
  });
  const { field: sameAsCurrentField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "mailingSameAsCurrent"),
  });

  // When checked, keep mailing fields in sync with physical reactively.
  useEffect(() => {
    if (!mailingSameAsCurrent) {
      return;
    }
    mailingStreetNumberField.onChange(physicalStreetNumber ?? "");
    mailingStreetNameField.onChange(physicalStreetName ?? "");
    mailingApartmentNumberField.onChange(physicalApartmentNumber ?? "");
    mailingZipField.onChange(physicalZip ?? "");
    mailingCityField.onChange(physicalCity ?? "");
    mailingStateField.onChange(physicalState ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately tracking the six physical values + checkbox
  }, [
    mailingSameAsCurrent,
    physicalStreetNumber,
    physicalStreetName,
    physicalApartmentNumber,
    physicalZip,
    physicalCity,
    physicalState,
  ]);

  /**
   * Handles the "Same as current" checkbox toggle; the mirroring effect reacts to the new value.
   * @param {boolean | "indeterminate"} checked - The checkbox's next checked state.
   */
  function onSameAsCurrentChange(checked: boolean | "indeterminate") {
    sameAsCurrentField.onChange(checked === true);
    // The useEffect above handles the actual mirroring reactively.
  }

  return { mailingSameAsCurrent: Boolean(mailingSameAsCurrent), onSameAsCurrentChange };
}
